import React from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { ChatMessage } from '../../types/chat';

interface MediaModalProps {
  visible: boolean;
  photo: { url: string; message: ChatMessage } | null;
  onClose: () => void;
}

const MediaModal: React.FC<MediaModalProps> = ({ visible, photo, onClose }) => {
  if (!photo) return null;

  return (
    <Dialog
      open={visible}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          boxShadow: 'none',
          maxWidth: '95vw',
          maxHeight: '95vh',
          width: 'auto',
          height: 'auto',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        sx={{
          position: 'relative',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'transparent',
          overflow: 'hidden',
          maxHeight: '95vh',
          maxWidth: '95vw',
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Media content - Image, Video, or Audio */}
        {(() => {
          const messageType = photo.message.messageType;
          const url = photo.url;
          
          // Check both messageType and URL pattern for audio
          const isAudio = messageType === 'audio' || url.includes('.m4a') || url.includes('.mp3') || url.includes('.wav');
          const isVideo = messageType === 'video' || url.includes('.mp4') || url.includes('.mov');
          
          if (isVideo && !isAudio) {
            return (
              <Box
                component="video"
                src={photo.url}
                controls
                autoPlay={false}
                sx={{
                  maxWidth: '90vw',
                  maxHeight: '80vh',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
                onError={(e) => {
                  console.error('Failed to load video:', photo.url);
                  e.currentTarget.style.display = 'none';
                }}
              />
            );
          } else if (isAudio) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 3,
                  p: 4,
                  minWidth: 400,
                  minHeight: 200,
                }}
              >
                {/* Audio icon */}
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="h2" sx={{ color: 'white' }}>
                    🎵
                  </Typography>
                </Box>
                
                {/* Audio player */}
                <audio
                  src={photo.url}
                  controls
                  autoPlay={false}
                  style={{
                    width: '100%',
                    maxWidth: '400px',
                    height: '54px',
                    backgroundColor: '#f0f0f0',
                  }}
                  onError={(e) => {
                    console.error('Failed to load audio:', photo.url);
                    e.currentTarget.style.display = 'none';
                  }}
                />
                
                {/* Audio info */}
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', textAlign: 'center' }}>
                  Audio Message
                  {photo.message.duration && (
                    <Typography component="span" sx={{ display: 'block', fontSize: '0.875rem', mt: 1 }}>
                      Duration: {photo.message.duration}
                    </Typography>
                  )}
                </Typography>
              </Box>
            );
          } else {
            return (
              <Box
                component="img"
                src={photo.url}
                alt="Full size"
                sx={{
                  maxWidth: '90vw',
                  maxHeight: '80vh',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
                onError={(e) => {
                  console.error('Failed to load image:', photo.url);
                  e.currentTarget.style.display = 'none';
                }}
              />
            );
          }
        })()}

        {/* Optional: Message info at bottom */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            right: 16,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            p: 2,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="body2">
            From: {photo.message.fromUser.firstName} {photo.message.fromUser.lastName}
          </Typography>
          <Typography variant="caption" sx={{ ml: 'auto' }}>
            {photo.message.createdOn.toLocaleString()}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MediaModal;
